import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fullAddress } from "@/lib/cases";
import { AuthCard } from "@/components/auth/AuthCard";
import { Tick } from "@/components/app/Tick";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const lbl = "mt-4 block font-mono-label text-[10px] font-semibold text-oath-secondary";
const primary = "mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-oath-ink px-6 py-3.5 font-mono-label text-[12px] font-semibold text-white hover:bg-black disabled:opacity-50";

const Identify = ({ token, info, onOk }) => {
  const [dob, setDob] = useState("");
  const [last4, setLast4] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.post(`/consent/${token}/identify`, { dob, phone_last4: last4 });
      onOk(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="consent-identify-form">
      <span className="grid h-11 w-11 place-items-center rounded-xl bg-oath-accent text-white"><ShieldCheck className="h-5 w-5" /></span>
      <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-oath-ink">Hi {info.first_name}</h1>
      <p className="mt-3 text-sm leading-relaxed text-oath-secondary" data-testid="consent-intro">
        <b>{info.org_name}</b> has asked ResidenceProof to verify your current home address for {info.purpose}. Nothing happens unless you agree.
        First, confirm it's you.
      </p>
      {err && <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="consent-identify-error">{err}</div>}
      <label className={lbl}>DATE OF BIRTH</label>
      <Input type="date" required value={dob} onChange={(e) => setDob(e.target.value)} className="mt-2" data-testid="consent-dob-input" />
      <label className={lbl}>LAST 4 DIGITS OF YOUR MOBILE NUMBER</label>
      <Input required inputMode="numeric" maxLength={4} value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))} className="mt-2 font-mono tracking-[0.4em]" data-testid="consent-last4-input" />
      <button type="submit" disabled={loading || last4.length !== 4 || !dob} className={primary} data-testid="consent-identify-submit">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "CONTINUE"}
      </button>
      <p className="mt-4 text-center font-mono text-[11px] text-oath-muted">Reference {info.case_number}</p>
    </form>
  );
};

const Decline = ({ token, session, onDone, onBack }) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      await api.post(`/consent/${token}/decline`, { consent_session: session, reason });
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div data-testid="consent-decline-panel">
      <h1 className="font-display text-2xl font-bold text-oath-ink">Decline this request?</h1>
      <p className="mt-3 text-sm text-oath-secondary">No verification will take place and the requesting company will be told you declined.</p>
      <label className={lbl}>REASON (OPTIONAL)</label>
      <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-2" data-testid="consent-decline-reason" />
      <button onClick={run} disabled={loading} className={primary.replace("bg-oath-ink", "bg-red-600").replace("hover:bg-black", "hover:bg-red-700")} data-testid="consent-decline-confirm">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "DECLINE"}
      </button>
      <button onClick={onBack} className="mt-3 w-full text-sm text-oath-secondary underline" data-testid="consent-decline-back">Go back</button>
    </div>
  );
};

const Sign = ({ token, d, onDone, onDecline }) => {
  const [agree, setAgree] = useState(false);
  const [agreeField, setAgreeField] = useState(false);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/consent/${token}/sign`, { consent_session: d.consent_session, signature_name: name, agree, agree_field: agreeField });
      onDone();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={run} data-testid="consent-sign-form">
      <h1 className="font-display text-2xl font-bold text-oath-ink">Review and sign</h1>
      <div className="mt-5 rounded-2xl border border-gray-200 bg-oath-bg/60 p-4 text-sm">
        <div className="font-mono-label text-[9px] font-semibold text-oath-muted">ADDRESS TO BE VERIFIED</div>
        <div className="mt-1 text-oath-ink" data-testid="consent-address">{fullAddress(d.address)}</div>
        <div className="mt-3 font-mono-label text-[9px] font-semibold text-oath-muted">PEOPLE SAID TO LIVE WITH YOU</div>
        <div className="mt-1 text-oath-ink" data-testid="consent-household">
          {d.household.length ? d.household.map((h) => `${h.name} (${h.relationship})`).join(", ") : "None declared"}
        </div>
      </div>
      <p className="mt-4 max-h-56 overflow-y-auto rounded-2xl border border-gray-200 p-4 text-xs leading-relaxed whitespace-pre-line text-oath-secondary" data-testid="consent-text">{d.consent_text}</p>
      <div className="mt-4"><Tick checked={agree} onToggle={() => setAgree(!agree)} label="I have read this and I give my consent" testid="consent-agree" /></div>
      {d.field_visit && <div className="mt-2"><Tick checked={agreeField} onToggle={() => setAgreeField(!agreeField)} label="I also authorize the exterior field visit described above" testid="consent-agree-field" /></div>}
      <label className={lbl}>TYPE YOUR FULL NAME TO SIGN: <span className="normal-case text-oath-ink">{d.full_name}</span></label>
      <Input required value={name} onChange={(e) => setName(e.target.value)} className="mt-2 font-display text-lg" data-testid="consent-signature-input" />
      <button type="submit" disabled={loading || !agree || (d.field_visit && !agreeField) || name.trim().length < 3} className={primary} data-testid="consent-sign-submit">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "SIGN AND GIVE CONSENT"}
      </button>
      <button type="button" onClick={onDecline} className="mt-3 w-full text-sm text-oath-secondary underline" data-testid="consent-decline-button">I do not consent</button>
    </form>
  );
};

const Final = ({ ok, text, testid }) => (
  <div className="text-center" data-testid={testid}>
    {ok ? <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" /> : <XCircle className="mx-auto h-10 w-10 text-slate-400" />}
    <p className="mt-4 text-sm leading-relaxed text-oath-secondary">{text}</p>
  </div>
);

const CLOSED = {
  consent_given: "You have already given consent for this request. Thank you.",
  consent_declined: "You declined this request. No verification will take place.",
  consent_expired: "This link has expired. Ask the company that contacted you for a new one.",
  consent_locked: "This link was locked after too many unsuccessful identity checks. Ask the company for a new one.",
  cancelled: "This request was cancelled by the company.",
};

export default function Consent() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [disclosure, setDisclosure] = useState(null);
  const [step, setStep] = useState("identify");

  useEffect(() => {
    api.get(`/consent/${token}`).then(({ data }) => setInfo(data)).catch((e) => setError(formatApiError(e)));
  }, [token]);

  let body;
  if (error) body = <Final text={error} testid="consent-error" />;
  else if (!info) body = <Loader2 className="h-5 w-5 animate-spin text-oath-muted" />;
  else if (step === "signed") body = <Final ok text="Thank you. Your consent has been recorded and the verification can begin. You can request a copy of your report or dispute it at any time." testid="consent-signed" />;
  else if (step === "declined") body = <Final text="You declined. No verification will take place." testid="consent-declined" />;
  else if (info.status !== "awaiting_consent") body = <Final ok={info.status === "consent_given"} text={CLOSED[info.status]} testid="consent-closed" />;
  else if (step === "decline") body = <Decline token={token} session={disclosure.consent_session} onDone={() => setStep("declined")} onBack={() => setStep("sign")} />;
  else if (disclosure) body = <Sign token={token} d={disclosure} onDone={() => setStep("signed")} onDecline={() => setStep("decline")} />;
  else body = <Identify token={token} info={info} onOk={(d) => { setDisclosure(d); setStep("sign"); }} />;

  return <AuthCard testid="consent-panel">{body}</AuthCard>;
}
